/**
 * UI State Store (Zustand)
 *
 * UI-only state that resets on navigation.
 * Server data should be managed by TanStack Query.
 */

import { create } from 'zustand'

export interface ModalState {
  type: 'create-license' | 'create-tenant' | 'create-extension' | 'generate-license' | 'approve-extension' | 'error' | 'none'
  entityId?: number
}

export interface FormStep {
  current: number
  total: number
}

interface UIState {
  // Sidebar
  sidebarOpen: boolean
  setSidebarOpen: (open: boolean) => void

  // Modal
  modalOpen: ModalState
  openModal: (type: ModalState['type'], entityId?: number) => void
  closeModal: () => void

  // Form Steps
  createLicenseStep: FormStep
  setCreateLicenseStep: (step: FormStep) => void
  resetCreateLicenseStep: () => void

  // Selection
  selectedLicenseId: number | null
  setSelectedLicenseId: (id: number | null) => void

  selectedTenantId: number | null
  setSelectedTenantId: (id: number | null) => void

  selectedExtensionId: string | null
  setSelectedExtensionId: (id: string | null) => void
}

export const useUIStore = create<UIState>((set) => ({
  // Sidebar state (default: closed on mobile, open on desktop)
  sidebarOpen: typeof window !== 'undefined' ? window.innerWidth >= 768 : false,
  setSidebarOpen: (open) => set({ sidebarOpen: open }),

  // Modal state
  modalOpen: { type: 'none' },
  openModal: (type, entityId) => set({ modalOpen: { type, entityId } }),
  closeModal: () => set({ modalOpen: { type: 'none' } }),

  // Form steps
  createLicenseStep: { current: 1, total: 4 },
  setCreateLicenseStep: (step) => set({ createLicenseStep: step }),
  resetCreateLicenseStep: () => set({ createLicenseStep: { current: 1, total: 4 } }),

  // Selection
  selectedLicenseId: null,
  setSelectedLicenseId: (id) => set({ selectedLicenseId: id }),

  selectedTenantId: null,
  setSelectedTenantId: (id) => set({ selectedTenantId: id }),

  selectedExtensionId: null,
  setSelectedExtensionId: (id) => set({ selectedExtensionId: id }),
}))
